// MARK://dhk8/pos/components/FilterableTable
// Componente genérico que extrae el patrón de "barra de filtros + tabla + pager"
// usado por TicketsPage (nui-filterbar + nui-table): búsqueda, filtro de estatus
// segmentado, rango de fechas (presets + personalizado), orden por columna y
// paginación cliente — todo re-utilizable para futuras páginas de listado
// (p.ej. la lista de retiros de caja / petty-cash) sin duplicar CSS ni lógica.
//
// Reparto de responsabilidades del rango de fechas: este componente NO asume
// dónde se resuelve. Si el padre pasa `onDateRangeChange`, el rango se emite
// hacia arriba (controlado) — típicamente el padre lo usa para refetch server-side
// (como hace Tickets). Si el padre pasa `dateAccessor` (y no `onDateRangeChange`),
// el componente filtra el rango internamente sobre `rows` (modo cliente). Ambos
// modos pueden coexistir con búsqueda/estatus, que SIEMPRE son filtros cliente.
//
// Puntos de extensión pensados para "crecer en opciones/botones/modales":
//   - `toolbarActions`: zona derecha de la barra de filtros, para botones nuevos.
//   - `toolbar`: zona libre adicional (encima/junto a la barra) para controles extra.
//   - `rowActions(row)`: columna de acciones por fila (botones, menús, etc).
//   - `columns[].render`: cualquier celda puede ser JSX custom (chips, iconos…).
//   - `renderSummary` / `summary`: bloque de resumen configurable (o ausente).

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

// ── Presets de rango de fechas (compartidos) ──────────────────────────────────
export type Preset = 'ALL' | 'TODAY' | 'YESTERDAY' | '7D' | '30D' | 'MONTH' | 'CUSTOM'

export const PRESET_OPTIONS: { value: Preset; label: string }[] = [
  { value: 'ALL', label: 'Todo' },
  { value: 'TODAY', label: 'Hoy' },
  { value: 'YESTERDAY', label: 'Ayer' },
  { value: '7D', label: 'Últimos 7 días' },
  { value: '30D', label: 'Últimos 30 días' },
  { value: 'MONTH', label: 'Este mes' },
  { value: 'CUSTOM', label: 'Personalizado…' },
]

export interface DateRange { fromIso?: string; toIso?: string }

export function computeDateRange(preset: Preset, customFrom: string, customTo: string): DateRange {
  const now = new Date()
  const startOfDay = (d: Date): Date => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x }
  const endOfDay = (d: Date): Date => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x }
  const daysAgo = (n: number): Date => { const x = new Date(now); x.setDate(x.getDate() - n); return x }
  switch (preset) {
    case 'TODAY': return { fromIso: startOfDay(now).toISOString(), toIso: endOfDay(now).toISOString() }
    case 'YESTERDAY': { const y = daysAgo(1); return { fromIso: startOfDay(y).toISOString(), toIso: endOfDay(y).toISOString() } }
    case '7D': return { fromIso: startOfDay(daysAgo(6)).toISOString(), toIso: endOfDay(now).toISOString() }
    case '30D': return { fromIso: startOfDay(daysAgo(29)).toISOString(), toIso: endOfDay(now).toISOString() }
    case 'MONTH': { const f = new Date(now.getFullYear(), now.getMonth(), 1); return { fromIso: startOfDay(f).toISOString(), toIso: endOfDay(now).toISOString() } }
    case 'CUSTOM': return {
      ...(customFrom ? { fromIso: new Date(`${customFrom}T00:00:00`).toISOString() } : {}),
      ...(customTo ? { toIso: new Date(`${customTo}T23:59:59.999`).toISOString() } : {}),
    }
    default: return {}
  }
}

// ── Chip de estatus (convención visual nui-chip) ──────────────────────────────
export type ChipTone = 'success' | 'warning' | 'danger' | 'neutral'

const CHIP_TONE_CLASS: Record<ChipTone, string> = {
  success: 'nui-chip--paid',
  warning: 'nui-chip--pre',
  danger: 'nui-chip--cancel',
  neutral: 'nui-chip--neutral',
}

/** Devuelve la clase `nui-chip …` para un tono dado — úsalo si quieres armar el <span> tú mismo. */
export function chipClass(tone: ChipTone): string {
  return `nui-chip ${CHIP_TONE_CLASS[tone]}`
}

export function StatusChip({ tone, children }: { tone: ChipTone; children: ReactNode }) {
  return <span className={chipClass(tone)}>{children}</span>
}

// ── Columnas ───────────────────────────────────────────────────────────────
export type ColumnAlign = 'left' | 'right'

export interface FilterableColumn<T> {
  key: string
  header: ReactNode
  /** Habilita orden por click en el header. Requiere sortAccessor. */
  sortable?: boolean
  /** Valor comparable para ordenar (string u number). */
  sortAccessor?: (row: T) => string | number
  align?: ColumnAlign
  /** Celda custom; por defecto intenta leer row[key]. */
  render?: (row: T) => ReactNode
  className?: string
  /** Reservado para layouts responsivos futuros (no aplica CSS todavía). */
  hideOnMobile?: boolean
}

export interface FilterableTableProps<T> {
  rows: T[]
  columns: FilterableColumn<T>[]
  getRowKey: (row: T) => string

  // Búsqueda (cliente).
  searchable?: boolean
  searchValue?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  searchAccessor?: (row: T) => string

  // Filtro de estatus / segmentado (genérico — NO hardcodeado a tickets).
  statusOptions?: { value: string; label: string }[]
  statusValue?: string
  onStatusChange?: (value: string) => void
  statusAccessor?: (row: T) => string
  /** Valor que representa "todos" en statusOptions/statusValue. Default 'ALL'. */
  statusAllValue?: string

  // Rango de fechas (presets + personalizado). El componente es dueño del estado
  // de preset/personalizado (uncontrolled) — no hace falta que el padre lo controle.
  dateRangeEnabled?: boolean
  /** Preset inicial. Default 'ALL'. */
  initialPreset?: Preset
  /**
   * Modo servidor: se invoca en mount y cada vez que el rango calculado cambia,
   * con (fromIso, toIso) ya resueltos — típicamente usado como parte de la
   * queryKey de un refetch server-side (así resuelve Tickets el rango hoy).
   */
  onDateRangeChange?: (from: string | undefined, to: string | undefined) => void
  /** Modo cliente: si se da (y no hay onDateRangeChange), el componente filtra `rows` por este accessor + el rango calculado. */
  dateAccessor?: (row: T) => string

  // Orden.
  initialSort?: { key: string; dir: 'asc' | 'desc' }

  // Paginación.
  pageSize?: number

  // Slots de crecimiento.
  toolbar?: ReactNode
  toolbarActions?: ReactNode
  onRowClick?: (row: T) => void
  rowActions?: (row: T) => ReactNode
  rowAriaLabel?: (row: T) => string

  // Resumen / estado.
  summary?: ReactNode
  renderSummary?: (filteredRows: T[]) => ReactNode
  loading?: boolean
  emptyState?: ReactNode
  ariaLabel?: string
}

export function FilterableTable<T>({
  rows,
  columns,
  getRowKey,
  searchable = false,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Buscar…',
  searchAccessor,
  statusOptions,
  statusValue,
  onStatusChange,
  statusAccessor,
  statusAllValue = 'ALL',
  dateRangeEnabled = false,
  initialPreset = 'ALL',
  onDateRangeChange,
  dateAccessor,
  initialSort,
  pageSize = 25,
  toolbar,
  toolbarActions,
  onRowClick,
  rowActions,
  rowAriaLabel,
  summary,
  renderSummary,
  loading = false,
  emptyState,
  ariaLabel,
}: FilterableTableProps<T>) {
  // Búsqueda / estatus / rango: si el padre no controla el valor, el componente
  // lleva su propio estado interno (uncontrolled) para poder usarse standalone.
  const [internalSearch, setInternalSearch] = useState('')
  const search = searchValue ?? internalSearch
  const setSearch = (v: string) => {
    if (onSearchChange) onSearchChange(v); else setInternalSearch(v)
    resetPage()
  }

  const [internalStatus, setInternalStatus] = useState(statusAllValue)
  const status = statusValue ?? internalStatus
  const setStatus = (v: string) => {
    if (onStatusChange) onStatusChange(v); else setInternalStatus(v)
    resetPage()
  }

  const [preset, setPresetState] = useState<Preset>(initialPreset)
  const [from, setFromState] = useState('')
  const [to, setToState] = useState('')
  const setPreset = (v: Preset) => { setPresetState(v); resetPage() }
  const setFrom = (v: string) => { setFromState(v); resetPage() }
  const setTo = (v: string) => { setToState(v); resetPage() }

  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(initialSort ?? null)
  const [page, setPage] = useState(0)
  const resetPage = () => setPage(0)

  const dateRange = useMemo(() => computeDateRange(preset, from, to), [preset, from, to])

  // Notifica al padre el rango resuelto (mount + cada cambio) — modo servidor.
  // "Latest ref" vía efecto (no se muta el ref durante el render): el efecto de
  // sincronización corre antes que el de notificación en cada commit, así que
  // el ref siempre trae el callback del render actual cuando se invoca.
  const onDateRangeChangeRef = useRef(onDateRangeChange)
  useEffect(() => { onDateRangeChangeRef.current = onDateRangeChange })
  useEffect(() => {
    if (dateRangeEnabled) onDateRangeChangeRef.current?.(dateRange.fromIso, dateRange.toIso)
  }, [dateRangeEnabled, dateRange.fromIso, dateRange.toIso])

  // ── Filtro cliente: estatus + búsqueda + (si aplica) fecha en modo cliente ──
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((row) => {
      if (statusOptions && statusAccessor && status !== statusAllValue && statusAccessor(row) !== status) return false
      if (q && searchAccessor && !searchAccessor(row).toLowerCase().includes(q)) return false
      // Filtro de fecha cliente: solo si NO estamos en modo servidor (sin onDateRangeChange) y hay dateAccessor.
      if (dateRangeEnabled && !onDateRangeChange && dateAccessor) {
        const t = +new Date(dateAccessor(row))
        if (dateRange.fromIso && t < +new Date(dateRange.fromIso)) return false
        if (dateRange.toIso && t > +new Date(dateRange.toIso)) return false
      }
      return true
    })
  }, [rows, status, statusOptions, statusAccessor, statusAllValue, search, searchAccessor, dateRangeEnabled, onDateRangeChange, dateAccessor, dateRange])

  const sorted = useMemo(() => {
    if (!sort) return filtered
    const col = columns.find((c) => c.key === sort.key)
    if (!col?.sortAccessor) return filtered
    const arr = filtered.slice()
    arr.sort((a, b) => {
      const va = col.sortAccessor!(a)
      const vb = col.sortAccessor!(b)
      let cmp: number
      if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb
      else cmp = String(va).localeCompare(String(vb), 'es', { numeric: true })
      return sort.dir === 'asc' ? cmp : -cmp
    })
    return arr
  }, [filtered, sort, columns])

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize))
  const safePage = Math.min(page, pageCount - 1)
  const visible = sorted.slice(safePage * pageSize, safePage * pageSize + pageSize)

  const toggleSort = (key: string) => {
    resetPage()
    setSort((cur) => (cur?.key === key ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))
  }
  const sortArrow = (key: string): string => (sort?.key === key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕')

  const clear = () => {
    setSearch('')
    setStatus(statusAllValue)
    setPreset('ALL')
    setFrom('')
    setTo('')
  }
  const hasActiveFilters = Boolean(search) || status !== statusAllValue || preset !== 'ALL'
  const colCount = columns.length + (rowActions ? 1 : 0)

  return (
    <div className="nui-ft-root">
      {/* ── Barra de filtros ── */}
      {(searchable || statusOptions || dateRangeEnabled || toolbar || toolbarActions) && (
        <div className="nui-filterbar">
          {toolbar}

          {searchable && (
            <div className="nui-filterbar__search">
              <span className="nui-filterbar__search-icon" aria-hidden="true">⌕</span>
              <input
                type="search"
                className="nui-filterbar__input"
                value={search}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}

          {statusOptions && (
            <label className="nui-filterbar__filter">
              <span className="nui-filterbar__filter-label">Estatus</span>
              <select
                className="nui-filterbar__select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                aria-label="Filtrar por estatus"
              >
                {statusOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          )}

          {dateRangeEnabled && (
            <>
              <label className="nui-filterbar__filter">
                <span className="nui-filterbar__filter-label">Periodo</span>
                <select
                  className="nui-filterbar__select"
                  value={preset}
                  onChange={(e) => setPreset(e.target.value as Preset)}
                  aria-label="Rango de fechas"
                >
                  {PRESET_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>

              {preset === 'CUSTOM' && (
                <>
                  <label className="nui-filterbar__filter">
                    <span className="nui-filterbar__filter-label">Desde</span>
                    <input
                      type="date"
                      className="nui-filterbar__select"
                      value={from}
                      max={to || undefined}
                      onChange={(e) => setFrom(e.target.value)}
                      aria-label="Fecha desde"
                    />
                  </label>
                  <label className="nui-filterbar__filter">
                    <span className="nui-filterbar__filter-label">Hasta</span>
                    <input
                      type="date"
                      className="nui-filterbar__select"
                      value={to}
                      min={from || undefined}
                      onChange={(e) => setTo(e.target.value)}
                      aria-label="Fecha hasta"
                    />
                  </label>
                </>
              )}
            </>
          )}

          {(hasActiveFilters || toolbarActions) && (
            <div className="nui-filterbar__actions">
              {hasActiveFilters && (
                <button type="button" className="nui-tk-clear" onClick={clear}>Limpiar</button>
              )}
              {toolbarActions}
            </div>
          )}
        </div>
      )}

      {/* ── Resumen ── */}
      {(summary || renderSummary) && (
        <div className="nui-tk-summary" role="status">
          {renderSummary ? renderSummary(filtered) : summary}
        </div>
      )}

      {/* ── Tabla ── */}
      <div className="nui-table-wrap">
        <div className="nui-table__scroll">
          <table className="nui-table" role="table" aria-label={ariaLabel}>
            <thead>
              <tr>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className={`nui-table__th${c.sortable ? ' nui-table__th--sortable' : ''}${c.className ? ' ' + c.className : ''}`}
                    scope="col"
                    style={c.align === 'right' ? { textAlign: 'right' } : undefined}
                    aria-sort={sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {c.sortable ? (
                      <button
                        type="button"
                        className="nui-table__sortbtn"
                        style={c.align === 'right' ? { justifyContent: 'flex-end' } : undefined}
                        onClick={() => toggleSort(c.key)}
                      >
                        <span>{c.header}</span>
                        <span className="nui-table__sortarrow" aria-hidden="true">{sortArrow(c.key)}</span>
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                ))}
                {rowActions && <th className="nui-table__th" scope="col" style={{ textAlign: 'right' }}>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, r) => (
                  <tr key={`sk-${r}`} className="nui-table__row nui-table__row--skeleton">
                    {Array.from({ length: colCount }).map((__, c) => (
                      <td key={c} className="nui-table__td"><span className="nui-table__skel" /></td>
                    ))}
                  </tr>
                ))
              ) : visible.length === 0 ? (
                <tr>
                  <td className="nui-table__empty" colSpan={colCount}>
                    {emptyState ?? (rows.length === 0 ? 'No hay registros.' : 'Ningún registro coincide con los filtros.')}
                  </td>
                </tr>
              ) : (
                visible.map((row) => {
                  const key = getRowKey(row)
                  const clickable = Boolean(onRowClick)
                  return (
                    <tr
                      key={key}
                      className={`nui-table__row${clickable ? ' nui-table__row--clickable' : ''}`}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      tabIndex={clickable ? 0 : undefined}
                      role={clickable ? 'button' : undefined}
                      aria-label={rowAriaLabel ? rowAriaLabel(row) : undefined}
                      onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row) } } : undefined}
                    >
                      {columns.map((c) => (
                        <td
                          key={c.key}
                          className={`nui-table__td${c.className ? ' ' + c.className : ''}`}
                          style={c.align === 'right' ? { textAlign: 'right' } : undefined}
                        >
                          {c.render ? c.render(row) : String((row as unknown as Record<string, unknown>)[c.key] ?? '')}
                        </td>
                      ))}
                      {rowActions && (
                        <td className="nui-table__td nui-tk-actions" onClick={(e) => e.stopPropagation()}>
                          {rowActions(row)}
                        </td>
                      )}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {pageCount > 1 && !loading && (
          <div className="nui-table__foot">
            <span className="nui-table__count">{sorted.length.toLocaleString('es-MX')} {sorted.length === 1 ? 'resultado' : 'resultados'}</span>
            <div className="nui-table__pager">
              <button type="button" className="nui-table__pagebtn" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={safePage === 0}>Anterior</button>
              <span className="nui-table__pageinfo">página {safePage + 1} de {pageCount}</span>
              <button type="button" className="nui-table__pagebtn" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={safePage >= pageCount - 1}>Siguiente</button>
            </div>
          </div>
        )}
      </div>

      <style>{filterableTableStyles}</style>
    </div>
  )
}

const filterableTableStyles = `
  .nui-ft-root { display:flex; flex-direction:column; gap:var(--pos-space-4,16px); min-height:0; }

  /* ── Filter bar (mismo look que Admin) ── */
  .nui-filterbar { display:flex; align-items:flex-end; gap:var(--pos-space-2,8px); flex-wrap:wrap; }
  .nui-filterbar__search { position:relative; display:flex; align-items:center; flex:1 1 220px; min-width:180px; }
  .nui-filterbar__search-icon { position:absolute; left:var(--pos-space-3,12px); color:var(--pos-fg-tertiary,#7E8C82); font-size:var(--pos-text-md,15px); pointer-events:none; line-height:1; }
  .nui-filterbar__input { width:100%; min-height:44px; padding:0 var(--pos-space-3,12px) 0 calc(var(--pos-space-3,12px) + 20px); border:1px solid var(--pos-separator,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-canvas,#0F1411); color:var(--pos-fg,#E8EFE9); font-family:var(--font-sans,inherit); font-size:var(--pos-text-sm,13px); }
  .nui-filterbar__input:focus { outline:none; border-color:var(--pos-tint,#1F8B3A); box-shadow:0 0 0 3px var(--pos-tint-soft,rgba(31,139,58,.18)); }
  .nui-filterbar__filter { display:flex; flex-direction:column; gap:4px; }
  .nui-filterbar__filter-label { font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-medium,500); color:var(--pos-fg-tertiary,#7E8C82); text-transform:uppercase; letter-spacing:var(--pos-tracking-wide,.04em); }
  .nui-filterbar__select { min-height:44px; padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-canvas,#0F1411); color:var(--pos-fg,#E8EFE9); font-family:var(--font-sans,inherit); font-size:var(--pos-text-sm,13px); cursor:pointer; }
  .nui-filterbar__select:focus { outline:none; border-color:var(--pos-tint,#1F8B3A); box-shadow:0 0 0 3px var(--pos-tint-soft,rgba(31,139,58,.18)); }
  .nui-filterbar__actions { display:flex; align-items:center; gap:var(--pos-space-2,8px); margin-left:auto; }
  .nui-tk-clear { min-height:44px; padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-elevated,#1E2520); color:var(--pos-fg-secondary,#B6BFB8); font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); font-weight:var(--pos-weight-medium,500); cursor:pointer; }
  .nui-tk-clear:hover { color:var(--pos-fg,#E8EFE9); background:var(--pos-surface,#161C18); }

  /* ── Resumen ── */
  .nui-tk-summary { display:flex; flex-wrap:wrap; align-items:baseline; gap:6px var(--pos-space-2,8px); padding:var(--pos-space-2,8px) var(--pos-space-3,12px); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-md,8px); background:var(--pos-surface,#161C18); font-family:var(--font-mono,ui-monospace,monospace); font-size:var(--pos-text-xs,12px); color:var(--pos-fg-secondary,#B6BFB8); font-variant-numeric:tabular-nums; }
  .nui-tk-summary strong { color:var(--pos-fg,#E8EFE9); font-weight:var(--pos-weight-semibold,600); }
  .nui-tk-summary em { font-style:normal; color:var(--pos-fg-tertiary,#7E8C82); }
  .nui-tk-summary__sep { color:var(--pos-fg-tertiary,#7E8C82); opacity:.6; }

  /* ── Tabla (mismo look que Admin nui-table) ── */
  .nui-table-wrap { display:flex; flex-direction:column; background:var(--pos-surface,#161C18); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-lg,12px); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); overflow:hidden; }
  .nui-table__scroll { width:100%; overflow-x:auto; }
  .nui-table { width:100%; border-collapse:collapse; font-family:var(--font-sans,inherit); font-size:var(--pos-text-sm,13px); color:var(--pos-fg,#E8EFE9); }
  .nui-table__th { position:sticky; top:0; z-index:1; background:var(--pos-elevated,#1E2520); color:var(--pos-fg-secondary,#B6BFB8); font-size:var(--pos-text-xs,12px); font-weight:var(--pos-weight-semibold,600); text-transform:uppercase; letter-spacing:var(--pos-tracking-wide,.04em); padding:var(--pos-space-2,8px) var(--pos-space-3,12px); white-space:nowrap; text-align:left; border-bottom:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); }
  .nui-table__sortbtn { display:inline-flex; align-items:center; gap:6px; width:100%; background:transparent; border:none; padding:0; margin:0; font:inherit; color:inherit; text-transform:inherit; letter-spacing:inherit; cursor:pointer; -webkit-tap-highlight-color:transparent; }
  .nui-table__sortarrow { font-size:9px; line-height:1; opacity:.4; font-family:var(--font-mono,var(--font-sans)); }
  .nui-table__row { border-bottom:1px solid var(--pos-separator,rgba(255,255,255,.07)); transition:background var(--pos-duration-fast,120ms) var(--pos-ease-out,ease); }
  .nui-table tbody tr:last-child { border-bottom:none; }
  .nui-table__row--clickable { cursor:pointer; }
  .nui-table__row--clickable:hover { background:var(--pos-elevated,#1E2520); }
  .nui-table__row--clickable:focus-visible { outline:none; box-shadow:inset 0 0 0 2px var(--pos-tint-soft,rgba(31,139,58,.35)); }
  .nui-table__td { padding:var(--pos-space-2,8px) var(--pos-space-3,12px); vertical-align:middle; white-space:nowrap; }
  .nui-table__empty { padding:var(--pos-space-8,32px); text-align:center; color:var(--pos-fg-tertiary,#7E8C82); font-size:var(--pos-text-sm,13px); }
  .nui-table__skel { display:block; height:12px; width:70%; border-radius:var(--pos-radius-xs,4px); background:linear-gradient(90deg,var(--pos-elevated,#1E2520) 25%,color-mix(in oklch,var(--pos-fg,#E8EFE9) 8%,var(--pos-elevated,#1E2520)) 37%,var(--pos-elevated,#1E2520) 63%); background-size:400% 100%; animation:nui-skel 1.4s ease infinite; }
  .nui-table__row--skeleton:hover { background:transparent; }
  @keyframes nui-skel { 0%{background-position:100% 50%} 100%{background-position:0 50%} }
  @media (prefers-reduced-motion: reduce) { .nui-table__skel { animation:none; } }
  .nui-table__foot { display:flex; align-items:center; justify-content:space-between; gap:var(--pos-space-3,12px); flex-wrap:wrap; padding:var(--pos-space-2,8px) var(--pos-space-3,12px); border-top:1px solid var(--pos-separator,rgba(255,255,255,.07)); background:var(--pos-surface,#161C18); }
  .nui-table__count { font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); font-family:var(--font-mono,ui-monospace,monospace); font-variant-numeric:tabular-nums; }
  .nui-table__pager { display:flex; align-items:center; gap:var(--pos-space-2,8px); }
  .nui-table__pageinfo { font-size:var(--pos-text-xs,12px); color:var(--pos-fg-secondary,#B6BFB8); font-variant-numeric:tabular-nums; white-space:nowrap; }
  .nui-table__pagebtn { display:inline-flex; align-items:center; justify-content:center; min-height:34px; padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-elevated,#1E2520); color:var(--pos-fg,#E8EFE9); font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); font-weight:var(--pos-weight-medium,500); cursor:pointer; }
  .nui-table__pagebtn:hover:not(:disabled) { background:var(--pos-surface,#161C18); }
  .nui-table__pagebtn:disabled { opacity:.5; cursor:not-allowed; }

  /* ── Acciones por fila (columna opcional) ── */
  .nui-tk-actions { white-space:nowrap; }

  /* ── Chips de estatus (convención compartida) ── */
  .nui-chip { display:inline-block; font-size:9px; font-weight:var(--pos-weight-semibold,600); letter-spacing:.06em; text-transform:uppercase; padding:2px var(--pos-space-2,8px); border-radius:var(--pos-radius-pill,999px); white-space:nowrap; }
  .nui-chip--paid { background:var(--pos-success-bg,rgba(31,139,58,.16)); color:var(--pos-success,#1F8B3A); }
  .nui-chip--pre { background:var(--pos-warning-bg,rgba(241,179,59,.18)); color:var(--pos-warning,#B8780B); border:1px solid color-mix(in oklch, var(--pos-warning,#B8780B) 30%, transparent); }
  .nui-chip--cancel { background:var(--pos-danger-bg,rgba(229,72,77,.14)); color:var(--pos-danger,#E5484D); }
  .nui-chip--neutral { background:var(--pos-elevated,#1E2520); color:var(--pos-fg-secondary,#B6BFB8); }
`
